import { CSSProperties, useMemo, useState } from 'react';
import OhatResultGraph, { Answers } from './OhatResultGraph';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button, Col, Row, Typography, message } from 'antd';
import { DownloadOutlined, UserOutlined } from '@ant-design/icons';
import OhatResultTable from './OhatResultTable';
import OhatResultHeading from './OhatResultHeading';
import { getNowDownloadLabel } from '../../../utils/date';
import { Patient } from '@models/patient';
import { useNavigate } from 'react-router-dom';
import useMobile from '../../../hooks/useMobile';
import OhatResultVerticalTable from './OhatResultVerticalTable';
import { useGAEvent } from '../../../hooks/useGAEvent';
import { OhatScore } from '@models/ohatScore';
import { extractAnswers, getTotalScore } from '../../../utils/ohat-score-utils';
import { ALL_QUESTIONS } from '../../../constants/question';
import { useTranslation } from 'react-i18next';
import { usePlan } from '../../../hooks/usePlan';
import { useCareProtocolFindAll } from '../../../libs/ohat-api';
import {
  CareProtocolItem,
  careProtocolKey,
} from '../../../types/care-protocol';

/** 項目別メモ・ケアプロトコルの1行（項目名と本文）。 */
type DetailRow = { key: string; label: string; content: string };

type DetailSectionProps = {
  heading: string;
  headingColor: string;
  borderColor: string;
  labelBackground: string;
  rows: DetailRow[];
};

/**
 * 項目別メモ・ケアプロトコルを、見出し＋2列の表として描画する（画面・PDF共通）。
 * 印刷でつぶれないよう文字を大きめにし、項目ごとに罫線で区切る。
 * antdのTableはhtml2canvasで崩れやすいため素の<table>で描く。
 */
function DetailSection(props: DetailSectionProps) {
  const { heading, headingColor, borderColor, labelBackground, rows } = props;
  // html2canvasは border-collapse の重なった罫線を太く描いてしまうため、
  // セルは右・下だけ、表は上・左だけに線を引いて1本の罫線に見せる。
  const cellStyle: CSSProperties = {
    borderRight: `1px solid ${borderColor}`,
    borderBottom: `1px solid ${borderColor}`,
    padding: '10px 12px',
    fontSize: 16,
    lineHeight: 1.6,
    verticalAlign: 'top',
    overflowWrap: 'anywhere',
  };
  return (
    <Row justify="center" style={{ marginBottom: 10 }}>
      {/* PDFの複製DOMでは data-pdf-memo の要素だけ幅を広げるため、
          メモ・プロトコルの両方に付けて幅を揃える。 */}
      <div data-pdf-memo style={{ width: '90%' }}>
        <Typography.Text style={{ fontSize: 18, color: headingColor }}>
          {heading}
        </Typography.Text>
        <table
          style={{
            width: '100%',
            marginTop: 6,
            borderCollapse: 'separate',
            borderSpacing: 0,
            borderTop: `1px solid ${borderColor}`,
            borderLeft: `1px solid ${borderColor}`,
            tableLayout: 'fixed',
          }}
        >
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td
                  style={{
                    ...cellStyle,
                    width: 136,
                    background: labelBackground,
                  }}
                >
                  {row.label}
                </td>
                <td style={{ ...cellStyle, whiteSpace: 'pre-wrap' }}>
                  {row.content}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Row>
  );
}

/**
 * フッター文言を描いたcanvasを作る。
 * jsPDFの標準フォントは日本語を描けないため、doc.text() ではなく画像として貼る。
 */
const createFooterCanvas = (text: string): HTMLCanvasElement | null => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return null;
  // PDFでは縮小して貼るので、大きめのフォントで描いて解像度を稼ぐ。
  const fontSize = 48;
  const fontFamily =
    window.getComputedStyle(document.body).fontFamily || 'sans-serif';
  let font = `${fontSize}px ${fontFamily}`;
  context.font = font;
  // フォント指定が解釈されなかった場合は既定値のままになるため、素のsans-serifに落とす。
  if (!context.font.includes(`${fontSize}px`)) {
    font = `${fontSize}px sans-serif`;
    context.font = font;
  }
  canvas.width = Math.max(1, Math.ceil(context.measureText(text).width));
  canvas.height = Math.ceil(fontSize * 1.4);
  // サイズ変更でコンテキストの状態が初期化されるため、描画設定を入れ直す。
  context.font = font;
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = '#8c8c8c';
  context.textBaseline = 'middle';
  context.fillText(text, 0, canvas.height / 2);
  return canvas;
};

/** 画面表示時の結果パネルの最大幅。広い画面で左右の余白を減らす。 */
const PANEL_MAX_WIDTH = 1200;
/**
 * PDFダウンロード中の結果パネルの最大幅。
 * PDFは実DOMを画像化してA4の印字幅に合わせて縮小するため、画面幅のままキャプチャすると
 * その分だけ文字が小さくなる。以前の改善（文字がつぶれる指摘への対応）と同じ文字サイズを
 * 保つため、キャプチャ時だけ800pxに戻す。
 */
const PDF_CAPTURE_MAX_WIDTH = 800;
/** 会員登録・ダウンロード・戻るボタン領域の最大幅。広い画面でもボタンが横長になりすぎないようにする。 */
const ACTIONS_MAX_WIDTH = 800;
/** PDF用の幅への再レイアウトを待つ上限時間（ミリ秒）。超えたらそのままキャプチャする。 */
const PDF_LAYOUT_WAIT_LIMIT_MS = 1500;

/**
 * PDFキャプチャ前に、結果パネルがPDF用の幅（800px）に縮み、
 * レーダーチャートがその幅で描き直されるまで待つ。
 * rechartsのResponsiveContainerはResizeObserverで幅を検知して非同期に再描画するため、
 * 待たずにキャプチャすると広い画面幅のままのグラフ（はみ出し・中央ずれ）が画像になる。
 * 条件が満たされなくても上限時間で打ち切り、無限に待たないようにする。
 */
const waitForPdfLayout = (target: HTMLElement): Promise<void> =>
  new Promise((resolve) => {
    const isReady = () => {
      if (target.getBoundingClientRect().width > PDF_CAPTURE_MAX_WIDTH + 0.5) {
        return false;
      }
      // 凡例アイコンにも recharts-surface が使われるため、チャート本体のsvgだけを見る。
      const surfaces = target.querySelectorAll<SVGSVGElement>(
        '.recharts-wrapper > svg.recharts-surface',
      );
      return Array.from(surfaces).every((svg) => {
        const container = svg.closest<HTMLElement>(
          '.recharts-responsive-container',
        );
        const svgWidth = Number(svg.getAttribute('width'));
        if (!container || !Number.isFinite(svgWidth)) return true;
        return (
          Math.abs(svgWidth - container.getBoundingClientRect().width) <= 1
        );
      });
    };
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(limitTimer);
      resolve();
    };
    // タブが非表示のときなどは requestAnimationFrame が止まるため、
    // 上限時間の打ち切りは setTimeout で別に保証する。
    const limitTimer = setTimeout(finish, PDF_LAYOUT_WAIT_LIMIT_MS);
    const check = () => {
      if (done) return;
      if (isReady()) {
        // 描き直した結果が確実に反映されるよう、もう1フレーム待ってから進む。
        requestAnimationFrame(finish);
        return;
      }
      requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });

type Props = {
  answers: Answers;
  isGuestMode: boolean;
  // 以下は、ゲストモードだと値はない
  oldOhatScores: OhatScore[];
  patient?: Patient;
};
export default function OhatResultPanel(props: Props) {
  const { answers, isGuestMode, patient, oldOhatScores } = props;
  const [isDownloading, setIsDownloading] = useState(false);
  const { isMobile } = useMobile();
  const { sendClickEvent } = useGAEvent();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const { canUse } = usePlan();
  // ケアプロトコルはライトプラン限定。ゲストモードでは取得しない。
  const showProtocols = !isGuestMode && canUse('care_protocol');
  const { data: careProtocolData } = useCareProtocolFindAll({
    query: { enabled: showProtocols },
  });
  // 項目×スコアごとの本文。キーは `item:score`。
  const protocolByItemScore = useMemo(() => {
    const map = new Map<string, string>();
    ((careProtocolData ?? []) as CareProtocolItem[]).forEach((p) => {
      if (p.content) map.set(careProtocolKey(p.item, p.score), p.content);
    });
    return map;
  }, [careProtocolData]);
  // 各項目のスコアに対応するケアプロトコルを表示する（スコア0は表示しない）。
  const scoreMap = answers.score as Record<number, number>;
  const protocolRows: DetailRow[] = showProtocols
    ? ALL_QUESTIONS.map((q, i) => ({
        key: `protocol-${i}`,
        label: `${q.key} ${q.getLabel(lang)}`,
        content:
          protocolByItemScore.get(careProtocolKey(i + 1, scoreMap[i])) ?? '',
      })).filter((r) => !!r.content)
    : [];
  const memoRows: DetailRow[] = ALL_QUESTIONS.map((q, i) => ({
    key: `memo-${i}`,
    label: `${q.key} ${q.getLabel(lang)}`,
    content: answers.memos?.[i]?.trim() ?? '',
  })).filter((r) => !!r.content);

  const oldAnswers = useMemo(() => {
    return oldOhatScores.map((score: OhatScore) => extractAnswers(score));
  }, [oldOhatScores]);

  const pdhDownloadHandler = async () => {
    sendClickEvent('PDFダウンロード');

    const target = document.getElementById('pdf-id');
    if (target === null) return;
    setIsDownloading(true);
    // パネルが800pxに縮み、グラフが描き直されるまで待ってからキャプチャする。
    await waitForPdfLayout(target);

    try {
      await document.fonts.ready;
      const doc = new jsPDF('p', 'mm', 'a4');
      const margin = 10;
      const pageHeight = doc.internal.pageSize.getHeight();
      // フッターの分だけ本文の高さを減らし、本文と重ならないようにする。
      const footerAreaHeight = 8;
      const availableWidth = doc.internal.pageSize.getWidth() - margin * 2;
      const availableHeight = pageHeight - margin * 2 - footerAreaHeight;
      const footerCanvas = createFooterCanvas(t('ohat.pdfFooter'));
      const capture = (element: HTMLElement) =>
        html2canvas(element, {
          useCORS: true,
          scrollY: -window.scrollY,
          backgroundColor: '#ffffff',
          onclone: (clonedDocument) => {
            // 備考欄は、2ページに分かれるときは1ページ目（#pdf-summary）の末尾に、
            // 1ページに収まるときは従来どおり最下部（#pdf-details）に出す。
            let remarksPlace = 'details';
            if (element.id === 'pdf-summary') remarksPlace = 'summary';
            if (element.id === 'pdf-details') remarksPlace = '';
            clonedDocument
              .querySelectorAll<HTMLElement>('[data-pdf-remarks]')
              .forEach((box) => {
                box.style.display =
                  box.dataset.pdfRemarks === remarksPlace ? 'block' : 'none';
              });
            // 画面では備考・ケアプロトコル欄と揃えて90%にしている比較表・備考欄を、
            // PDFでは印字可能幅いっぱいに広げ、メモ表（[data-pdf-memo]）と左右端を揃える。
            clonedDocument
              .querySelectorAll<HTMLElement>('[data-pdf-full-width]')
              .forEach((box) => {
                box.style.width = '100%';
              });
            const details = clonedDocument.getElementById('pdf-details');
            if (details) {
              // PDF用の複製だけでメモ欄を余白いっぱいまで広げる。
              details
                .querySelectorAll<HTMLElement>('[data-pdf-memo]')
                .forEach((memo) => {
                  memo.style.width = '100%';
                });
              if (element.id === 'pdf-details') {
                // 2ページに分かれたときだけ、2ページ目の先頭にも見出しを出す。
                details
                  .querySelectorAll<HTMLElement>('[data-pdf-heading]')
                  .forEach((heading) => {
                    heading.style.display = 'block';
                  });
              }
            }
            // キャプチャ対象（#pdf-id / #pdf-summary / #pdf-details）が縦長だと、
            // addPageImage の縮小率が高さ基準になり、画像が印字幅より細くなって左右に大きな余白が出る。
            // そこで複製DOMだけ、縦横比がA4の印字領域に収まるまで対象の横幅を広げて再配置する。
            // 縮小率は高さ基準のまま（内容の高さはほぼ変わらない）なので文字サイズは変わらず、
            // 表・ケアプロトコル・備考欄が横に伸びて左右の余白が埋まる。
            const clonedTarget = clonedDocument.getElementById(element.id);
            if (!clonedTarget) return;
            // 初期幅は広げる前の実幅で測る（flex を外すと内容幅に変わるため先に測る）。
            let width = Math.max(
              720,
              clonedTarget.getBoundingClientRect().width,
            );
            // #pdf-id は antd の Col（flex: 0 0 100% / max-width: 100%）なので、
            // 幅指定が効くよう flex と max-width も外す。
            clonedTarget.style.maxWidth = 'none';
            clonedTarget.style.flex = 'none';
            clonedTarget.style.width = `${width}px`;
            // 今の高さからA4の印字領域の縦横比を満たす幅を直接計算して広げる。
            // 広げると内容が再配置されて高さが減るため、収束するまで繰り返す（通常2〜3回）。
            // 比率をちょうど満たす幅で止まるので、高さ基準の縮小率（文字サイズ）が保たれる。
            for (let attempt = 0; attempt < 12; attempt++) {
              const bounds = clonedTarget.getBoundingClientRect();
              const needed = (bounds.height * availableWidth) / availableHeight;
              if (bounds.width >= needed - 0.5) break;
              width = Math.ceil(needed);
              clonedTarget.style.width = `${width}px`;
            }
            // 複製DOMではReactが動かないため、rechartsのsvgは元の幅（約768px）のまま残り、
            // 広げた領域の左に寄ってしまう。グラフ本体（凡例は内部の絶対配置）を中央に寄せる。
            // recharts v3 は ResponsiveContainer と wrapper の間に幅0の div を挟むため、
            // その間の要素を100%幅にしてから wrapper を margin: auto で中央に置く。
            clonedTarget
              .querySelectorAll<HTMLElement>('.recharts-wrapper')
              .forEach((wrapper) => {
                const container = wrapper.closest(
                  '.recharts-responsive-container',
                );
                if (!container) return;
                wrapper.style.margin = '0 auto';
                let parent = wrapper.parentElement;
                while (parent && parent !== container) {
                  parent.style.width = '100%';
                  parent = parent.parentElement;
                }
              });
          },
        });
      // 全ページの左下に共通のフッターを入れる。
      const addFooter = () => {
        if (!footerCanvas) return;
        const height = footerAreaHeight / 2;
        const width = (footerCanvas.width / footerCanvas.height) * height;
        doc.addImage(
          footerCanvas.toDataURL('image/png'),
          'PNG',
          margin,
          pageHeight - margin - height,
          Math.min(width, availableWidth),
          height,
        );
      };
      const addPageImage = (canvas: HTMLCanvasElement) => {
        const ratio = Math.min(
          availableWidth / canvas.width,
          availableHeight / canvas.height,
        );
        const width = canvas.width * ratio;
        doc.addImage(
          canvas.toDataURL('image/png'),
          'PNG',
          margin + (availableWidth - width) / 2,
          margin,
          width,
          canvas.height * ratio,
        );
        addFooter();
      };
      const canvas = await capture(target);
      const hasMemos = memoRows.length > 0;
      // メモなしは必ず1ページ。メモありでも横幅基準で収まれば1ページにする。
      if (
        !hasMemos ||
        (canvas.height * availableWidth) / canvas.width <= availableHeight
      ) {
        addPageImage(canvas);
      } else {
        // グラフと表は分離しない。詳細も1ページに収め、合計2ページを上限にする。
        const summary = document.getElementById('pdf-summary');
        const details = document.getElementById('pdf-details');
        if (!summary || !details) throw new Error('PDF sections not found');
        addPageImage(await capture(summary));
        doc.addPage();
        addPageImage(await capture(details));
      }
      doc.save(`ohat_${getNowDownloadLabel()}.pdf`);
    } catch (error) {
      console.error('PDF download failed', error);
      message.error(t('ohat.downloadFailed'));
    } finally {
      setIsDownloading(false);
    }
  };
  const onClick = () => {
    navigate(-1); // navigate(`/patients/${patient!.id}`);
  };

  const onClickRegister = () => {
    sendClickEvent('会員登録');
    navigate(`/signup`);
  };

  // 備考欄（記入用の空枠）。どこに出すかは onclone で切り替えるため、
  // 画面表示では常に非表示にしておく。
  const renderRemarksBox = (place: 'summary' | 'details') => (
    <div
      data-pdf-remarks={place}
      style={{ display: 'none', paddingTop: 25, paddingBottom: 25 }}
    >
      {/* PDFでは onclone で比較表・メモ表と同じ100%幅に広げる。 */}
      <div
        data-pdf-full-width
        style={{
          height: 100,
          width: '90%',
          margin: '0 auto',
          padding: 10,
          border: '1px solid gray',
        }}
      >
        <Typography.Text>{t('ohat.remarks')}</Typography.Text>
      </div>
    </div>
  );

  return (
    <Row
      gutter={[16, 8]}
      justify="center"
      style={{
        maxWidth: isDownloading ? PDF_CAPTURE_MAX_WIDTH : PANEL_MAX_WIDTH,
      }}
    >
      <Col span={24} id="pdf-id">
        <div id="pdf-summary" style={{ display: 'flow-root' }}>
          <Typography.Title
            level={2}
            underline
            style={{
              textAlign: 'center',
            }}
          >
            {t('ohat.resultTitle')}
          </Typography.Title>
          <OhatResultHeading
            score={getTotalScore(answers)}
            evaluatedAt={answers.evaluatedAt}
            patient={isGuestMode ? undefined : patient}
            showCareLevel
          />
          <Col
            span={24}
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <OhatResultGraph answers={answers} oldAnswers={oldAnswers} />
          </Col>
          <Row justify="center" style={{ marginBottom: 10, marginTop: 15 }}>
            {/* 画面では備考・ケアプロトコル欄と同じ幅（親の90%）に揃える。
                PDFの複製DOMでは data-pdf-full-width で100%に広げる。 */}
            <div data-pdf-full-width style={{ width: '90%' }}>
              {isMobile() ? (
                <OhatResultVerticalTable
                  answers={answers}
                  oldAnswers={oldAnswers}
                />
              ) : (
                <OhatResultTable answers={answers} oldAnswers={oldAnswers} />
              )}

              <Typography.Text style={{ color: '#a9a9a9', fontSize: 12 }}>
                {t('ohat.caution')}
              </Typography.Text>
            </div>
          </Row>
          {isDownloading && renderRemarksBox('summary')}
        </div>
        <div id="pdf-details" style={{ display: 'flow-root' }}>
          {isDownloading && (
            // 2ページに分かれたときだけ onclone で表示する見出し。
            <div data-pdf-heading style={{ display: 'none', marginBottom: 10 }}>
              <OhatResultHeading
                score={getTotalScore(answers)}
                evaluatedAt={answers.evaluatedAt}
                patient={isGuestMode ? undefined : patient}
              />
            </div>
          )}
          {memoRows.length > 0 && (
            <DetailSection
              heading={t('ohat.itemMemo')}
              headingColor="#03007E"
              borderColor="#d9d9d9"
              labelBackground="#fafafa"
              rows={memoRows}
            />
          )}
          {protocolRows.length > 0 && (
            <DetailSection
              heading={t('careProtocol.resultHeading')}
              headingColor="#d46b08"
              borderColor="#ffd591"
              labelBackground="#fff7e6"
              rows={protocolRows}
            />
          )}
          {isDownloading && renderRemarksBox('details')}
        </div>
      </Col>
      {/* 会員登録・ダウンロード・戻るボタンは、パネルを1200pxに広げても横長になりすぎないよう
          従来どおり最大800pxで中央に置く（表・グラフの #pdf-id だけを広げる）。 */}
      <Col span={24} style={{ maxWidth: ACTIONS_MAX_WIDTH }}>
        <Row gutter={[16, 8]} justify="center">
          {isGuestMode && (
            <Col
              xs={24}
              md={24}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  // background: '#a9a9a9',
                  padding: 20,
                  borderRadius: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Button
                  type="primary"
                  size="large"
                  shape="round"
                  onClick={onClickRegister}
                  // antdのボタンは高さ固定のため、大きい文字に合わせて高さを自動にして上下中央に置く
                  style={{
                    height: 'auto',
                    paddingTop: 12,
                    paddingBottom: 12,
                  }}
                >
                  <>
                    <UserOutlined
                      style={{ fontSize: 26, color: 'white', marginRight: 8 }}
                    />
                    <Typography.Text style={{ fontSize: 26, color: 'white' }}>
                      {t('ohat.signup')}
                    </Typography.Text>
                  </>
                </Button>
                <Typography.Text style={{ fontSize: 16, marginTop: 20 }}>
                  {t('ohat.signupLead')}
                </Typography.Text>
              </div>
            </Col>
          )}

          <Col
            xs={24}
            md={10}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <Button
              type="default"
              size="large"
              shape="round"
              loading={isDownloading}
              icon={<DownloadOutlined />}
              onClick={pdhDownloadHandler}
              block
            >
              {t('ohat.downloadResult')}
            </Button>
          </Col>

          <Col
            xs={24}
            md={10}
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            <Button size="large" shape="round" onClick={onClick} block>
              {t('ohat.back')}
            </Button>
          </Col>
        </Row>
      </Col>
      <Col span={24} style={{ marginTop: 20 }}></Col>
    </Row>
  );
}
